import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { Link } from "react-router-dom";

const navLinks = [
  {
    label: "About us",
    href: "#",
    dropdown: [
      { label: "About Gundeti Foundation", href: "#" },
      { label: "Our Partners", href: "#" },
      { label: "Impact Stories", href: "#" },
    ],
  },
  { label: "Meet the team", href: "/team" },
  { label: "Events", href: "/events" },
  { label: "Gallery", href: "/gallery" },
  { label: "Contact us", href: "/contact" },
];

// ---------------- Mega Menu for Desktop ----------------
function MegaMenuDropdown({
  anchorEl,
  open,
  onMouseEnter,
  onMouseLeave,
  dropdown,
}) {
  if (!open || !anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const style = {
    position: "absolute",
    left: rect.left + window.scrollX,
    top: rect.bottom + window.scrollY,
    width: 160,
    background: "white",
    color: "var(--color-black)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
    borderRadius: 8,
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
  };
  return ReactDOM.createPortal(
    <div style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {dropdown.map((subitem) => (
        <a
          key={subitem.label}
          href={subitem.href}
          style={{
            color: "var(--color-black)",
            fontWeight: 500,
            fontSize: "0.9rem",
            padding: "6px 0",
            textDecoration: "none",
          }}
          onMouseDown={onMouseLeave}
        >
          {subitem.label}
        </a>
      ))}
    </div>,
    document.body
  );
}

const Navbar = ({ onContactClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // for mobile collapsible
  const [megaMenuOpen, setMegaMenuOpen] = useState(null);
  const [megaMenuAnchor, setMegaMenuAnchor] = useState(null);
  const megaMenuTimeout = useRef();

  // toggle drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // mobile dropdown toggle
  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  // desktop mega menu
  const handleMegaMenuMouseEnter = (event, label) => {
    if (megaMenuTimeout.current) clearTimeout(megaMenuTimeout.current);
    setMegaMenuAnchor(event.currentTarget);
    setMegaMenuOpen(label);
  };
  const handleMegaMenuMouseLeave = () => {
    megaMenuTimeout.current = setTimeout(() => {
      setMegaMenuOpen(null);
      setMegaMenuAnchor(null);
    }, 180);
  };

  // contact click - now links to contact page instead of scrolling to footer
  const handleContactClick = () => {
    setMobileOpen(false);
  };

  // ---------------- Drawer (Mobile) ----------------
  const drawer = (
    <Box sx={{ width: 260, p: 2, fontFamily: "Poppins, Arial, sans-serif" }}>
      {/* Logo */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <img src="/logo192.png" alt="logo" style={{ height: 32, marginRight: 8 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "var(--color-accent-blue)" }}
        >
          Gundeti Foundation
        </Typography>
      </Box>

      {/* Search in drawer */}


      {/* Navigation */}
      <List>
        {navLinks.map((item) =>
          item.dropdown ? (
            <React.Fragment key={item.label}>
              <ListItem button onClick={() => handleDropdownToggle(item.label)}>
                <ListItemText primary={item.label} />
                {openDropdown === item.label ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={openDropdown === item.label} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.dropdown.map((subitem) => (
                    <ListItemButton
                      key={subitem.label}
                      component="a"
                      href={subitem.href}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText primary={subitem.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ) : (
            <ListItemButton
              key={item.label}
              component={item.label === "Events" || item.label === "Contact us" || item.label === "Gallery" ? Link : "a"}
              to={item.label === "Events" || item.label === "Contact us" || item.label === "Gallery" ? item.href : undefined}
              href={item.label !== "Events" && item.label !== "Contact us" && item.label !== "Gallery" ? item.href : undefined}
              onClick={item.label === "Contact us" ? handleContactClick : undefined}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          )
        )}
      </List>

      {/* Donate button */}
      <Box sx={{ mt: 3, textAlign: "center" }}>
      <Button
  variant="contained"
  href="#donate"
  sx={{
    py: 1.2,
    px: 4, // add horizontal padding
    fontWeight: 800,
    borderRadius: 999,
    background: "var(--color-accent-blue)",
    color: "white",
  }}
>
  Donate Now
</Button>

      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "white",
          borderBottom: "1px solid var(--color-light-blue)",
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 6 }, justifyContent: "space-between" }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
            <img src="/logo192.png" alt="logo" style={{ height: 36, marginRight: 10 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "var(--color-accent-blue)" }}
            >
              Gundeti Foundation
            </Typography>
          </Link>

          {/* Desktop menu */}
          <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 2.5 }}>
            {navLinks.map((item) =>
              item.dropdown ? (
                <Box
                  key={item.label}
                  onMouseEnter={(e) => handleMegaMenuMouseEnter(e, item.label)}
                  onMouseLeave={handleMegaMenuMouseLeave}
                >
                  <Button
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: "var(--color-black)",
                      "&:hover": {
                        color: "var(--color-accent-blue)",
                        background: "var(--color-light-blue)",
                      },
                    }}
                  >
                    {item.label}
                    <ArrowDropDownIcon
                      sx={{
                        fontSize: 18,
                        ml: 0.3,
                        transform:
                          megaMenuOpen === item.label ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </Button>
                  <MegaMenuDropdown
                    anchorEl={megaMenuAnchor}
                    open={megaMenuOpen === item.label}
                    onMouseEnter={() => setMegaMenuOpen(item.label)}
                    onMouseLeave={handleMegaMenuMouseLeave}
                    dropdown={item.dropdown}
                  />
                </Box>
              ) : (
                <Button
                  key={item.label}
                  component={item.label === "Events" || item.label === "Contact us" || item.label === "Gallery" ? Link : "a"}
                  to={item.label === "Events" || item.label === "Contact us" || item.label === "Gallery" ? item.href : undefined}
                  href={item.label !== "Events" && item.label !== "Contact us" && item.label !== "Gallery" ? item.href : undefined}
                  onClick={item.label === "Contact us" ? handleContactClick : undefined}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "var(--color-black)",
                    "&:hover": {
                      color: "var(--color-accent-blue)",
                      background: "var(--color-light-blue)",
                    },
                  }}
                >
                  {item.label}
                </Button>
              )
            )}
          </Box>

          {/* Donate btn desktop */}
          <Box sx={{ display: { xs: "none", lg: "flex" } }}>
            <Button
              variant="contained"
              href="#donate"
              sx={{
                px: 3,
                py: 1.2,
                fontWeight: 700,
                borderRadius: 999,
                background: "var(--color-accent-blue)",
              }}
            >
              Donate Now
            </Button>
          </Box>

          {/* Mobile toggle */}
          <IconButton onClick={handleDrawerToggle} sx={{ display: { lg: "none" } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: 260 },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Navbar;
